import React from "react";
import { buildPaymentCsv } from "../../utils/paymentCsv";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Button,
  DatePicker,
  Form,
  Popconfirm,
  Table,
  Typography,
  Select,
  Modal,
  Tag,
  Space,
  message,
} from "antd";
import { FilePdfOutlined, EyeOutlined } from "@ant-design/icons";
import { AppDispatch, RootState } from "../../store";
import { DownloadOutlined } from "@ant-design/icons";
import moment, { Moment } from "moment";
import axios from "axios";
import { useTranslation } from "react-i18next";
import SummariesExportExcel from "./SentSummariesExportExcel";
import { formInitialValues } from "../../@types/cost";

import EditableRow from "./EditableRow";
import EditableCell from "./EditableCell";
import EditableCellAll from "./EditableCellAll";
import { useParams } from "react-router-dom";
import { loadApartments } from "../../store/apartmentsSlice";

type EditableTableProps = Parameters<typeof Table>[0];
type ColumnTypes = Exclude<EditableTableProps["columns"], undefined>;

const SentSummaries: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const apartments = useSelector(
    (state: RootState) => state.apartments.apartments
  );
  const [filterApartments, setFilterApartments] = useState<any>([]);
  const [filterUsers, setFilterUsers] = useState<any>([]);
  const [filterBusinessSegment, setFilterBusinessSegment] = useState<string[]>(
    []
  );
  const [listSummaries, setListSummaries] = useState<any[]>([]);
  const [dateFrom, setDateFrom] = useState<Moment | null>(
    moment().startOf("month")
  );
  const [dateTo, setDateTo] = useState<Moment | null>(moment().endOf("month"));
  const [selectedACsRowKeys, setSelectedACsRowKeys] = useState<Array<any>>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const params = useParams();

  const user = useSelector((state: RootState) => state.auth.user);
  const users = useSelector((state: RootState) => state.users);

  const verifyUser = user?.Role === "admin" || user?.Role === "super-admin";
  const [t] = useTranslation("common");

  const [businessSegment, setBusinessSegment] = useState([]);
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string | null>(null);
  const [selectedPdfFilename, setSelectedPdfFilename] = useState<string | null>(null);
  const [selectedPdfId, setSelectedPdfId] = useState<number | null>(null);
  const [loadingPdfId, setLoadingPdfId] = useState<number | null>(null);

  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const isEditing = (record: any) => record.ID === editingKey;

  const test = async () => {
    const values = await form.getFieldsValue();
    const { OwnerChargeSplit, CostValue } = values;
    const newCostValue = Number(OwnerChargeSplit) * Number(CostValue);
    form.setFieldsValue({ ...values, OwnerCostValue: newCostValue.toFixed(2) });
  };

  const components = {
    body: {
      row: editingKey ? undefined : EditableRow,
      cell: editingKey ? EditableCellAll : EditableCell,
    },
  };

  const edit = (record: any) => {
    form.setFieldsValue({ ...formInitialValues, ...record });
    setEditingKey(record.ID);
  };

  const handleAdd = () => {
    const arrayId = listSummaries.map(({ ID }) => Number(ID)) || [];
    const newId = Math.max(...arrayId) + 1;
    const newCost = { ...formInitialValues, ID: newId };
    setListSummaries([...listSummaries, newCost]);
    edit(newCost);
  };

  const fetchListSentSummaries = async (
    overrides?: { page?: number; pageSize?: number }
  ) => {
    const page = overrides?.page ?? currentPage;
    const size = overrides?.pageSize ?? pageSize;
    setListLoading(true);
    try {
      const res = await axios
        .get("/sentsummaries/list", {
          params: {
            apartments: filterApartments,
            users: filterUsers,
            businessSegment: filterBusinessSegment,
            from: dateFrom ? dateFrom.format("YYYY-MM-DD") : "",
            to: dateTo ? dateTo.format("YYYY-MM-DD") : "",
            ...(params.ownerId ? { ownerId: params.ownerId } : {}),
            page,
            pageSize: size,
          },
        })
        .then((res) => res.data);

      setListSummaries(Array.isArray(res?.rows) ? res.rows : []);
      setTotal(typeof res?.total === "number" ? res.total : 0);
    } catch (err) {
      console.log(err);
    } finally {
      setListLoading(false);
    }
  };

  const applyFilters = () => {
    if (currentPage === 1) {
      fetchListSentSummaries({ page: 1 });
    } else {
      setCurrentPage(1); // useEffect on currentPage will refetch
    }
  };

  const deleteSummaries = async (ID: string | number) => {
    try {
      await axios["delete"](`/sentsummaries/${ID}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      fetchListSentSummaries();
    } catch (err) {
      console.log(err);
    }
  };

  const save = async (key: string | number) => {
    try {
      const row = await form.validateFields();
      const verifyDate = {
        ...row,
      };
      await axios["post"](
        `/sentsummaries/create`,
        {
          ...verifyDate,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      ).then(() => {
        fetchListSentSummaries();
        setEditingKey("");
      });
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const cancel = () => {
    fetchListSentSummaries();
    setEditingKey("");
  };

  const columns: (ColumnTypes[number] & {
    editable?: boolean;
    dataIndex: string;
  })[] = [
    {
      title: t("sentsummaries.table.ID"),
      dataIndex: "ID",
      defaultSortOrder: "ascend",
      width: 50,
      sorter: (a: any, b: any) =>
        (a.ID as unknown as string) > (b.ID as unknown as string) ? 1 : -1,
    },
    {
      title: t("sentsummaries.table.SentON"),
      dataIndex: "SentON",
      width: 140,
      editable: verifyUser,
      sorter: (a: any, b: any) => {
        const dateA = moment(a.SentON).valueOf();
        const dateB = moment(b.SentON).valueOf();
        return dateA - dateB;
      },
      render: (SentON: string) => {
        if (!SentON) return <span className="text-gray-400">-</span>;
        
        const sentDate = moment(SentON);
        const now = moment();
        const isToday = sentDate.isSame(now, 'day');
        const isYesterday = sentDate.isSame(now.clone().subtract(1, 'day'), 'day');
        const isThisWeek = sentDate.isSame(now, 'week');
        const isThisYear = sentDate.isSame(now, 'year');
        
        let formattedDate: string;
        let timeAgo: string;
        
        if (isToday) {
          formattedDate = sentDate.format("HH:mm");
          timeAgo = "Today";
        } else if (isYesterday) {
          formattedDate = sentDate.format("HH:mm");
          timeAgo = "Yesterday";
        } else if (isThisWeek) {
          formattedDate = sentDate.format("ddd HH:mm");
          timeAgo = sentDate.fromNow();
        } else if (isThisYear) {
          formattedDate = sentDate.format("MMM DD HH:mm");
          timeAgo = sentDate.fromNow();
        } else {
          formattedDate = sentDate.format("YYYY-MM-DD HH:mm");
          timeAgo = sentDate.fromNow();
        }
        
        return (
          <div className="whitespace-nowrap">
            <div className="font-medium text-gray-900 dark:text-white">
              {formattedDate}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {timeAgo}
            </div>
          </div>
        );
      },
    },
    {
      title: t("sentsummaries.table.SentBy"),
      dataIndex: "SentBy",
      editable: verifyUser,
      width: 150,
      sorter: (a: any, b: any) =>
        (a.SentBy as string) > (b.SentBy as string) ? 1 : -1,
      render: (SentBy: string) => {
        return <span className="whitespace-nowrap">{SentBy}</span>;
      },
    },
    {
      title: <div>{t("sentsummaries.table.RoomName")}</div>,
      dataIndex: "RoomName",
      editable: verifyUser,
      width: 200,
      sorter: (a: any, b: any) =>
        (a.RoomName as string) > (b.RoomName as string) ? 1 : -1,
      render: (RoomName: string) => {
        return <span className="whitespace-nowrap">{RoomName}</span>;
      },
    },
    {
      title: <div>{t("sentsummaries.table.ParkingName")}</div>,
      dataIndex: "ParkingName",
      editable: verifyUser,
      width: 200,
      sorter: (a: any, b: any) =>
        (a.ParkingName as string) > (b.ParkingName as string) ? 1 : -1,
      render: (ParkingName: string) => {
        return <span className="whitespace-nowrap">{ParkingName}</span>;
      },
    },
    {
      title: t("sentsummaries.table.DateFrom"),
      dataIndex: "DateFrom",
      editable: verifyUser,
      width: 120,
      sorter: (a: any, b: any) =>
        (a.DateFrom as number) > (b.DateFrom as number) ? 1 : -1,
      render: (DateFrom: string) => {
        return (
          <span className="whitespace-nowrap">
            {DateFrom ? moment(DateFrom).format("YYYY-MM-DD") : ""}
          </span>
        );
      },
    },
    {
      title: <div>{t("sentsummaries.table.DateTo")}</div>,
      dataIndex: "DateTo",
      editable: verifyUser,
      width: 120,
      sorter: (a: any, b: any) =>
        (a.DateTo as number) > (b.DateTo as number) ? 1 : -1,
      render: (DateTo: string) => {
        return (
          <span className="whitespace-nowrap">
            {DateTo ? moment(DateTo).format("YYYY-MM-DD") : ""}
          </span>
        );
      },
    },

    {
      title: t("sentsummaries.table.OwnerName"),
      dataIndex: "OwnerName",
      editable: true,
      width: 90,
      sorter: (a: any, b: any) =>
        (a.OwnerName as number) > (b.OwnerName as number) ? 1 : -1,
      render: (OwnerName: string) => {
        return <span className="whitespace-nowrap">{OwnerName}</span>;
      },
    },
    {
      title: t("sentsummaries.table.ApartmentTotal"),
      dataIndex: "ApartmentTotal",
      width: 150,
      editable: verifyUser,
      sorter: (a: any, b: any) =>
        (a.ApartmentTotal as string) > (b.ApartmentTotal as string) ? 1 : -1,
      render: (ApartmentTotal: string) => {
        return <span className="whitespace-nowrap">{ApartmentTotal}</span>;
      },
    },
    {
      title: t("sentsummaries.table.CostTotal"),
      dataIndex: "CostTotal",
      width: 200,
      editable: verifyUser,
      sorter: (a: any, b: any) =>
        (a.CostTotal as string) > (b.CostTotal as string) ? 1 : -1,
      render: (CostTotal: string) => {
        return <span className="whitespace-nowrap">{CostTotal}</span>;
      },
    },
    {
      title: t("sentsummaries.table.parkingTotal"),
      dataIndex: "parkingTotal",
      width: 200,
      editable: verifyUser,
      sorter: (a: any, b: any) =>
        (a.parkingTotal as string) > (b.parkingTotal as string) ? 1 : -1,
      render: (parkingTotal: string) => {
        return <span className="whitespace-nowrap">{parkingTotal}</span>;
      },
    },
    {
      title: t("sentsummaries.table.FinalTotal"),
      dataIndex: "FinalTotal",
      width: 200,
      editable: verifyUser,
      sorter: (a: any, b: any) =>
        (a.FinalTotal as string) > (b.FinalTotal as string) ? 1 : -1,
      render: (FinalTotal: string) => {
        return <span className="whitespace-nowrap">{FinalTotal}</span>;
      },
    },
    {
      title: t("sentsummaries.table.BusinessSegment"),
      dataIndex: "BusinessSegment",
      width: 200,
      editable: verifyUser,
      sorter: (a: any, b: any) =>
        (a.BusinessSegment as string) > (b.BusinessSegment as string) ? 1 : -1,
      render: (BusinessSegment: string) => {
        return <span className="whitespace-nowrap">{BusinessSegment}</span>;
      },
    },
    {
      title: t("sentsummaries.table.SourceAccount"),
      dataIndex: "SourceAccount",
      width: 200,
      editable: verifyUser,
      sorter: (a: any, b: any) =>
        (a.SourceAccount as string) > (b.SourceAccount as string) ? 1 : -1,
      render: (SourceAccount: string) => {
        return <span className="whitespace-nowrap">{SourceAccount}</span>;
      },
    },
    {
      title: t("sentsummaries.table.DestinationAccount"),
      dataIndex: "DestinationAccount",
      width: 200,
      editable: verifyUser,
      sorter: (a: any, b: any) =>
        (a.DestinationAccount as string) > (b.DestinationAccount as string)
          ? 1
          : -1,
      render: (DestinationAccount: string) => {
        return <span className="whitespace-nowrap">{DestinationAccount}</span>;
      },
    },
    {
      title: "Status",
      dataIndex: "Status",
      width: 120,
      sorter: (a: any, b: any) => {
        const statusOrder = { pending: 0, sent: 1, delivered: 2, failed: 3, bounced: 4, complained: 5, exported: 6 };
        return (statusOrder[a.Status as keyof typeof statusOrder] || 99) - 
               (statusOrder[b.Status as keyof typeof statusOrder] || 99);
      },
      render: (Status: string, record: any) => {
        if (!Status) return <Tag>Unknown</Tag>;
        
        const statusConfig: Record<string, { color: string; text: string }> = {
          pending: { color: "orange", text: "Pending" },
          sent: { color: "blue", text: "Sent" },
          delivered: { color: "green", text: "Delivered" },
          failed: { color: "red", text: "Failed" },
          bounced: { color: "red", text: "Bounced" },
          complained: { color: "purple", text: "Complained" },
          exported: { color: "cyan", text: "Exported" },
        };

        const config = statusConfig[Status.toLowerCase()] || { color: "default", text: Status };
        
        return (
          <div>
            <Tag color={config.color}>{config.text}</Tag>
            {record.DeliveredDT && Status === "delivered" && (
              <div className="text-xs text-gray-500 mt-1">
                {moment(record.DeliveredDT).format("MMM DD, HH:mm")}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "PDF",
      dataIndex: "HasPDF",
      width: 100,
      align: "center",
      render: (_: any, record: any) => {
        if (!record.HasPDF && !record.AttachmentFilename) {
          return <span className="text-gray-400">-</span>;
        }
        const isLoading = loadingPdfId === record.ID;
        return (
          <div className="flex justify-center items-center">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={async () => {
                try {
                  setLoadingPdfId(record.ID);
                  setSelectedPdfId(record.ID);
                  setSelectedPdfFilename(record.AttachmentFilename || "attachment.pdf");
                  
                  // Fetch PDF using axios (includes auth token)
                  const response = await axios.get(`/sentsummaries/${record.ID}/pdf`, {
                    responseType: 'blob',
                  });
                  
                  // Create blob URL for iframe
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const blobUrl = URL.createObjectURL(blob);
                  setSelectedPdfUrl(blobUrl);
                  setPdfModalVisible(true);
                } catch (err) {
                  console.error("Error loading PDF:", err);
                  message.error("Failed to load PDF");
                } finally {
                  setLoadingPdfId(null);
                }
              }}
              size="small"
              loading={isLoading}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              View PDF
            </Button>
          </div>
        );
      },
    },
    {
      title: "operation",
      dataIndex: "operation",
      render: (_, record: any) => {
        const editable = isEditing(record);

        return listSummaries.length >= 1 && verifyUser && editable ? (
          <span>
            <Typography.Link
              onClick={() => save(record.ID)}
              style={{
                marginRight: 8,
              }}
            >
              Save
            </Typography.Link>
            <Popconfirm
              title="Sure to cancel?"
              onConfirm={cancel}
              okType="danger"
            >
              <button>Cancel</button>
            </Popconfirm>
          </span>
        ) : listSummaries.length >= 1 && verifyUser ? (
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => deleteSummaries(record.ID)}
            okType="danger"
          >
            <Button key="delete" className="btn-danger">
              {t("DELETE")}
            </Button>
          </Popconfirm>
        ) : null;
      },
    },
  ];

  useEffect(() => {
    dispatch(loadApartments({ search: "", ownerId: "" }));
    axios.get(`/sentsummaries/bs/`).then((res) => {
      setBusinessSegment(res.data);
    });
  }, []);

  useEffect(() => {
    fetchListSentSummaries();
    // Refetch whenever paging changes; initial mount also fires here with
    // currentPage=1, pageSize=50.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize]);

  const onDateChange = (dates: any) => {
    setDateFrom(dates ? dates[0] : null);
    setDateTo(dates ? dates[1] : null);
  };

  const disabledDate = (current: any) => {
    return current && current < moment("2021-12-31").endOf("day");
  };

  const showColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }

    return {
      ...col,
      onCell: (record: any) => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
        test: () => test(),
        handleSave: () => fetchListSentSummaries(),
      }),
    };
  });

  function exportCSV() {
    const result = buildPaymentCsv(listSummaries, selectedACsRowKeys);
    if (result.errors.length) {
      Modal.error({
        title: t("paymentExport.blocked"),
        content: (
          <ul>
            {result.errors.map((error, index) => (
              <li key={index}>{error.id ? "#" + error.id + ": " : ""}{t("paymentExport." + error.reason)}</li>
            ))}
          </ul>
        ),
      });
      return;
    }
    const url = URL.createObjectURL(new Blob([result.csv], { type: "text/csv;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = "PLATNOSCI_IMPORT_" + moment().format("YYYYMMDDHHmmss") + "_BalticHomeMiedzyzdroje.csv";
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(link);
  }

  return (
    <div className="container-xl mx-auto px-3 h-full pt-7 flex flex-col">
      <div className="mt-8 border-b mb-2 border-gray-400 lg:flex justify-between">
        <div className="flex items-center mb-2">
          <span className="font-bold mr-4">{t("transactions.Period")}:</span>

          <DatePicker.RangePicker
            ranges={{
              "This Month": [
                moment().startOf("month"),
                moment().endOf("month"),
              ],
            }}
            value={[dateFrom, dateTo]}
            onChange={onDateChange}
            disabledDate={verifyUser ? () => {} : disabledDate}
          />
          <Select
            className="w-40 ml-3"
            mode="multiple"
            allowClear
            placeholder="All"
            onChange={(value) => setFilterApartments(value)}
            value={filterApartments}
          >
            <Select.Option value="none">None</Select.Option>
            {apartments.map((apartment: any) => (
              <Select.Option
                key={apartment.RoomName}
                value={apartment.RoomName}
              >
                {apartment.RoomName}
              </Select.Option>
            ))}
          </Select>
          <Select
            className="w-40 ml-3"
            mode="multiple"
            allowClear
            placeholder="All"
            onChange={(value) => setFilterUsers(value)}
            value={filterUsers}
          >
            <Select.Option value="none">None</Select.Option>
            {users.owners.map((user: any) => (
              <Select.Option
                key={user.OwnerID}
                value={user.FirstName + " " + user.LastName}
              >
                {user.FirstName + " " + user.LastName}
              </Select.Option>
            ))}
          </Select>
          <Select
            className="w-40 ml-3"
            mode="multiple"
            allowClear
            placeholder="All"
            onChange={(value) => setFilterBusinessSegment(value)}
            value={filterBusinessSegment}
          >
            <Select.Option value="none">None</Select.Option>
            {businessSegment.map(
              (item: {
                RowID: number;
                BusinessSegment: string;
                AccountNumber: string;
              }) => (
                <Select.Option key={item.RowID} value={item.BusinessSegment}>
                  {item.BusinessSegment}
                </Select.Option>
              )
            )}
          </Select>
          <Button
            className="btn-default h-8 ml-2"
            onClick={() => applyFilters()}
          >
            {t("Submit")}
          </Button>
        </div>
        <Button
          className="btn-default hvr-float-shadow h-10 w-40 ml-3"
          icon={<DownloadOutlined className="align-middle" />}
          onClick={() => {
            exportCSV();
          }}
          style={{ margin: "10px 0" }}
        >
          {t("EXPORT CSV")}
        </Button>
      </div>

      <div className="max-w-full overflow-auto">
        <Form form={form} component={false}>
          <Table
            components={components}
            rowKey="ID"
            columns={showColumns as ColumnTypes}
            dataSource={listSummaries}
            rowClassName={"hover:bg-white hover:bg-opacity-10"}
            className="border flex-grow"
            loading={listLoading}
            pagination={{
              current: currentPage,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ["25", "50", "100", "200", "1000"],
              showTotal: (t) => `${t} total`,
              onChange: (page, size) => {
                if (size !== pageSize) {
                  setPageSize(size);
                  setCurrentPage(1);
                } else {
                  setCurrentPage(page);
                }
              },
            }}
            rowSelection={{
              selectedRowKeys: selectedACsRowKeys,
              onChange: (values) => {
                setSelectedACsRowKeys(values);
              },
            }}
          />
        </Form>
      </div>
      <div
        className="flex"
        style={{ width: "100%", justifyContent: "flex-end", marginTop: "10px" }}
      >
        {verifyUser && (
          <Button
            disabled={editingKey !== ""}
            onClick={handleAdd}
            type="primary"
            className="btn-yellow hvr-float-shadow h-10 w-40 ml-3"
            style={{ marginBottom: 16 }}
          >
            Add a row
          </Button>
        )}
        <SummariesExportExcel
          rows={listSummaries}
          dateFrom={dateFrom}
          dateTo={dateTo}
        />
      </div>

      {/* PDF Viewer Modal */}
      <Modal
        title={
          <Space>
            <FilePdfOutlined />
            <span>{selectedPdfFilename || "PDF Viewer"}</span>
          </Space>
        }
        visible={pdfModalVisible}
        onCancel={() => {
          setPdfModalVisible(false);
          // Clean up blob URL to prevent memory leaks
          if (selectedPdfUrl) {
            URL.revokeObjectURL(selectedPdfUrl);
          }
          setSelectedPdfUrl(null);
          setSelectedPdfFilename(null);
          setSelectedPdfId(null);
        }}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setPdfModalVisible(false);
                // Clean up blob URL to prevent memory leaks
                if (selectedPdfUrl) {
                  URL.revokeObjectURL(selectedPdfUrl);
                }
                setSelectedPdfUrl(null);
                setSelectedPdfFilename(null);
                setSelectedPdfId(null);
              }}
              style={{
                minWidth: '100px',
                height: '34px',
                fontSize: '14px',
                fontWeight: 500,
                backgroundColor: '#ffffff',
                borderColor: '#d9d9d9',
                color: '#262626',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fafafa';
                e.currentTarget.style.borderColor = '#40a9ff';
                e.currentTarget.style.color = '#40a9ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#ffffff';
                e.currentTarget.style.borderColor = '#d9d9d9';
                e.currentTarget.style.color = '#262626';
              }}
            >
              Close
            </Button>
            {selectedPdfUrl && selectedPdfFilename && selectedPdfId && (
              <Button
                type="primary"
                onClick={async () => {
                  try {
                    const response = await axios.get(`/sentsummaries/${selectedPdfId}/pdf`, {
                      responseType: 'blob',
                    });
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = selectedPdfFilename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  } catch (err) {
                    console.error("Error downloading PDF:", err);
                    message.error("Failed to download PDF");
                  }
                }}
                icon={<DownloadOutlined />}
                style={{
                  minWidth: '130px',
                  height: '34px',
                  fontSize: '14px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 500,
                  backgroundColor: '#1890ff',
                  borderColor: '#1890ff',
                  color: '#ffffff',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#40a9ff';
                  e.currentTarget.style.borderColor = '#40a9ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#1890ff';
                  e.currentTarget.style.borderColor = '#1890ff';
                }}
              >
                Download
              </Button>
            )}
          </Space>
        }
        width={900}
        style={{ top: 20 }}
        bodyStyle={{ height: "80vh", padding: 0 }}
      >
        {selectedPdfUrl && (
          <iframe
            src={selectedPdfUrl}
            style={{
              width: "100%",
              height: "100%",
              border: "none",
            }}
            title="PDF Viewer"
          />
        )}
      </Modal>
    </div>
  );
};

export default SentSummaries;
